import React from 'react';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/material/styles';
import { SceneContainer } from './shared';

// --- Keyframe animations ---

const ballRoll = keyframes`
  0% { transform: translateX(-80px) rotate(0deg); }
  45% { transform: translateX(60px) rotate(540deg); }
  55% { transform: translateX(60px) rotate(540deg); }
  60% { transform: translateX(50px) rotate(560deg); }
  100% { transform: translateX(-80px) rotate(0deg); }
`;

const cueStrike = keyframes`
  0% { transform: translateX(0) rotate(-2deg); }
  25% { transform: translateX(-30px) rotate(-1deg); }
  35% { transform: translateX(10px) rotate(-2deg); }
  45%, 100% { transform: translateX(0) rotate(-2deg); }
`;

const targetBallHit = keyframes`
  0%, 55% { transform: translateX(0) scale(1); opacity: 1; }
  60% { transform: translateX(0) scale(1.1); opacity: 1; }
  70% { transform: translateX(30px) scale(1); opacity: 1; }
  85% { transform: translateX(50px) scale(0.95); opacity: 0.8; }
  90% { transform: translateX(55px) scale(0.6); opacity: 0; }
  100% { transform: translateX(0) scale(1); opacity: 1; }
`;

const pocketFlash = keyframes`
  0%, 85% { opacity: 0; transform: scale(0.8); }
  90% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0; transform: scale(0.8); }
`;

const chalkDust = keyframes`
  0%, 30% { opacity: 0; }
  35% { opacity: 0.6; transform: scale(1); }
  60% { opacity: 0; transform: scale(2); }
  100% { opacity: 0; }
`;

const scoreGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const textFade = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

const COLORS = {
  baize: '#10B981',
  baizeDark: '#059669',
  baizeText: '#047857', // WCAG AA compliant on white (~5.6:1)
  cushion: '#065F46',
  wood: '#92400E',
  woodLight: '#B45309',
  red: '#DC2626',
  yellow: '#FBBF24',
  white: '#F5F5F4',
  black: '#1C1917',
  gold: '#F59E0B',
};

interface SnookerLoadingAnimationProps {
  message?: string;
}

const SnookerLoadingAnimationBase: React.FC<SnookerLoadingAnimationProps> = ({
  message = 'Setting up the table...',
}) => {
  return (
    <SceneContainer>
      {/* Main scene SVG */}
      <svg
        width="300"
        height="180"
        viewBox="0 0 300 180"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="presentation"
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {/* Table surface */}
        <rect x="30" y="40" width="240" height="120" rx="6" fill={COLORS.baize} />

        {/* Baize texture lines */}
        {[50, 70, 90, 110, 130, 150].map((y) => (
          <line
            key={y}
            x1="35"
            y1={y}
            x2="265"
            y2={y}
            stroke={COLORS.baizeDark}
            strokeWidth="0.5"
            opacity={0.3}
          />
        ))}

        {/* Cushion rails */}
        <rect x="30" y="40" width="240" height="6" rx="2" fill={COLORS.cushion} />
        <rect x="30" y="154" width="240" height="6" rx="2" fill={COLORS.cushion} />
        <rect x="30" y="40" width="6" height="120" rx="2" fill={COLORS.cushion} />
        <rect x="264" y="40" width="6" height="120" rx="2" fill={COLORS.cushion} />

        {/* Wood border */}
        <rect x="25" y="35" width="250" height="135" rx="8" stroke={COLORS.wood} strokeWidth="3" fill="none" />

        {/* Corner pockets */}
        {[
          { cx: 32, cy: 42 },
          { cx: 268, cy: 42 },
          { cx: 32, cy: 158 },
          { cx: 268, cy: 158 },
        ].map((p, i) => (
          <circle key={i} cx={p.cx} cy={p.cy} r="7" fill={COLORS.black} />
        ))}

        {/* Middle pockets */}
        <circle cx="150" cy="40" r="6" fill={COLORS.black} />
        <circle cx="150" cy="160" r="6" fill={COLORS.black} />

        {/* Baulk line */}
        <line x1="100" y1="46" x2="100" y2="154" stroke={COLORS.baizeDark} strokeWidth="1" opacity={0.5} />

        {/* D semicircle */}
        <path d="M100 80 Q85 100 100 120" stroke={COLORS.baizeDark} strokeWidth="1" fill="none" opacity={0.5} />

        {/* Cue stick */}
        <g style={{
          transformOrigin: '90px 100px',
          animation: `${cueStrike} 3.5s ease-in-out infinite`,
        }}>
          <line x1="20" y1="102" x2="115" y2="99" stroke={COLORS.woodLight} strokeWidth="4" strokeLinecap="round" />
          <line x1="105" y1="99.5" x2="118" y2="99" stroke="#E7E5E4" strokeWidth="3" strokeLinecap="round" />
          {/* Chalk tip */}
          <circle cx="118" cy="99" r="2" fill={COLORS.baize} />
        </g>

        {/* Chalk dust puff */}
        <g style={{ animation: `${chalkDust} 3.5s ease-out infinite` }}>
          <circle cx="128" cy="98" r="6" fill={COLORS.baize} opacity={0.3} />
          <circle cx="132" cy="95" r="4" fill={COLORS.baize} opacity={0.2} />
          <circle cx="126" cy="94" r="3" fill={COLORS.baize} opacity={0.2} />
        </g>

        {/* Cue ball (white) - rolling */}
        <g style={{
          transformOrigin: '140px 100px',
          animation: `${ballRoll} 3.5s ease-in-out infinite`,
        }}>
          <circle cx="140" cy="100" r="8" fill={COLORS.white} />
          {/* Shine */}
          <circle cx="137" cy="97" r="2.5" fill="white" opacity={0.6} />
          {/* Rotation mark */}
          <circle cx="142" cy="102" r="1" fill="#D6D3D1" opacity={0.5} />
        </g>

        {/* Target ball (red) - gets hit */}
        <g style={{
          transformOrigin: '210px 100px',
          animation: `${targetBallHit} 3.5s ease-in-out infinite`,
        }}>
          <circle cx="210" cy="100" r="8" fill={COLORS.red} />
          <circle cx="207" cy="97" r="2.5" fill="white" opacity={0.3} />
        </g>

        {/* Pocket flash when ball sinks */}
        <g style={{ animation: `${pocketFlash} 3.5s ease-out infinite` }}>
          <circle cx="268" cy="100" r="12" fill={COLORS.gold} opacity={0.4} />
          <circle cx="268" cy="100" r="8" fill={COLORS.gold} opacity={0.2} />
        </g>

        {/* Static balls for scene depth */}
        <circle cx="180" cy="80" r="7" fill={COLORS.red} opacity={0.6} />
        <circle cx="195" cy="75" r="7" fill={COLORS.red} opacity={0.6} />
        <circle cx="188" cy="88" r="7" fill={COLORS.yellow} opacity={0.6} />
        <circle cx="200" cy="84" r="7" fill={COLORS.red} opacity={0.6} />

        {/* Score indicator */}
        <g style={{ animation: `${scoreGlow} 2s ease-in-out infinite` }}>
          <rect x="130" y="15" width="40" height="16" rx="4" fill={COLORS.cushion} />
          <text
            x="150"
            y="26"
            textAnchor="middle"
            fill={COLORS.gold}
            fontSize="10"
            fontWeight="700"
            fontFamily="'IBM Plex Sans', sans-serif"
          >
            147
          </text>
        </g>
      </svg>

      {/* Loading text */}
      <Typography
        variant="body2"
        sx={{
          mt: 1.5,
          color: COLORS.baizeText,
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 600,
          letterSpacing: '0.04em',
          animation: `${textFade} 2.5s ease-in-out infinite`,
        }}
      >
        {message}
      </Typography>
    </SceneContainer>
  );
};

export const SnookerLoadingAnimation = React.memo(SnookerLoadingAnimationBase);
